import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "@fontsource/ibm-plex-sans/400.css";
import "@fontsource/ibm-plex-sans/700.css";
import {
  Camera,
  CameraSlash,
  ChartLineUp,
  EnvelopeSimple,
  Globe,
  IdentificationCard,
  LinkedinLogo,
  MicrosoftTeamsLogo,
  UsersThree,
  WhatsappLogo,
  YoutubeLogo,
} from "@phosphor-icons/react";
import pageSettled from "../../rendering/pageSettled";
import { CONTENT, useContent } from "./content";
import { useHomeLang } from "./homeLang";
import { GoogleMeetLogo, ZoomLogo } from "./icons";
import PrompterStrip from "./PrompterStrip";
import StudioScene, { SCENES } from "./StudioScene";
import useCamera from "./useCamera";
import "./studio.css";

// Keyed by the need's id (content.js).
const TEMPLATE_ICONS = {
  update: ChartLineUp,
  intro: IdentificationCard,
  family: UsersThree,
  markets: Globe,
};

const templatesOf = (lang) => CONTENT[lang].NEEDS.map((n, i) => ({ ...n, index: i }))
  .filter((n) => n.studio)
  .map((n) => {
    const Icon = TEMPLATE_ICONS[n.id];
    const scene = SCENES[n.id];
    if (!Icon || !scene) throw new Error(`The "${n.id}" template has no studio icon or scene`);
    const prompt = scene.prompt[lang];
    if (!prompt) throw new Error(`The "${n.id}" template has no ${lang} teleprompter script`);
    return { ...n, Icon, scene, prompt, label: n.studioChip || n.template, title: n.studioTitle || n.sceneTitle };
  });
// The templates the studio offers, in each language.
const STUDIO_TEMPLATES = { it: templatesOf("it"), en: templatesOf("en") };

// The studio's own words, in each language; `problems` explains each reason
// useCamera gives for the camera not turning on.
const WORDS = {
  it: {
    heading: "Personalizza il tuo studio",
    lead: "Scegli un modello pronto e mettici nome, ruolo, colore e disclaimer. In diretta o registrato, il prompter ti suggerisce cosa dire.",
    name: "Il tuo nome",
    role: "Ruolo",
    otherRole: "Altro: scrivilo tu",
    ownRole: "Il tuo ruolo",
    template: "Modello",
    brand: "Colore del tuo brand",
    swatch: (color) => `Colore ${color}`,
    disclaimer: "Disclaimer che scorre nel video",
    seenBy: (others) => `Così ti vedranno gli altri ${others}.`,
    record: "Registra il tuo primo video",
    live: "In diretta, come webcam",
    sent: "Registrato, da inviare",
    tryIt: "Guardati nello studio",
    asking: "Consenti l’accesso…",
    retry: "Riprova",
    private: "Il video resta sul tuo dispositivo: non registriamo nulla.",
    turnOff: "Spegni la fotocamera",
    problems: {
      blocked: "Hai negato l’accesso alla fotocamera. Puoi riattivarlo dalle impostazioni del sito, accanto alla barra dell’indirizzo.",
      missing: "Non troviamo nessuna fotocamera collegata.",
      busy: "La fotocamera è già in uso da un’altra app. Chiudila e riprova.",
      unsupported: "Questo browser non permette di usare la fotocamera da questa pagina.",
      failed: "Non siamo riusciti ad accendere la fotocamera. Riprova.",
    },
  },
  en: {
    heading: "Customize your studio",
    lead: "Pick a ready-made template and add your name, role, brand color and disclaimer. Live or recorded, the teleprompter feeds you your lines.",
    name: "Your name",
    role: "Role",
    otherRole: "Other: type your own",
    ownRole: "Your role",
    template: "Template",
    brand: "Your brand color",
    swatch: (color) => `${color} color`,
    disclaimer: "Disclaimer that scrolls across the video",
    seenBy: (others) => `This is how the other ${others} will see you.`,
    record: "Record your first video",
    live: "Live, as your webcam",
    sent: "Recorded, ready to send",
    tryIt: "See yourself in the studio",
    asking: "Allow access…",
    retry: "Try again",
    private: "The video stays on your device. We don’t record anything.",
    turnOff: "Turn off the camera",
    problems: {
      blocked: "You’ve blocked access to your camera. You can turn it back on in the site settings, next to the address bar.",
      missing: "We can’t find a connected camera.",
      busy: "Your camera is already in use by another app. Close it and try again.",
      unsupported: "This browser doesn’t let this page use your camera.",
      failed: "We couldn’t turn on your camera. Try again.",
    },
  },
};

// The name the preview shows until the visitor types their own, and the
// role it shows until they type theirs after choosing "Altro".
const SAMPLE_NAME = "Sergio Gentili";
const SAMPLE_ROLE = "Family banker";
// The role choice past the listed ones, which opens a field for their own.
// Each language lists as many roles, in the same order.
const OWN_ROLE = CONTENT.it.ROLES.length;
if (CONTENT.en.ROLES.length !== OWN_ROLE) throw new Error("The studio lists a different number of roles in English and Italian");

// What the visitor has set up: the template (its index in NEEDS), the role
// and brand colour they picked, and their name, own role and disclaimer as
// typed. The disclaimer starts as a typical one for them to rewrite as their
// network's; the ticker runs it as it stands, and none when it is empty.
const firstLook = ({ DISCLAIMER }, templates) => ({ template: templates[templates.length - 1].index, name: "", role: 0, ownRole: "", disclaimer: DISCLAIMER, color: 0 });

const roleOf = (look, roles) => (look.role === OWN_ROLE ? look.ownRole.trim() || SAMPLE_ROLE : roles[look.role]);

const NO_CAMERA = { status: "off" };

// The frame a look composes, with the visitor's camera in it or not.
function LookScene({ look, camera = NO_CAMERA }) {
  const { ROLES, BRAND_COLORS } = useContent();
  const chosen = STUDIO_TEMPLATES[useHomeLang()].find((t) => t.index === look.template);
  if (!chosen) throw new Error(`No studio template has the index ${look.template}`);
  return (
    <StudioScene
      scene={chosen.scene}
      title={chosen.title}
      name={look.name.trim() || SAMPLE_NAME}
      role={roleOf(look, ROLES)}
      brand={BRAND_COLORS[look.color].hex}
      disclaimer={look.disclaimer.trim()}
      camera={camera}
    />
  );
}

// Where the finished studio goes: live as a webcam, or recorded and sent.
const LIVE_APPS = [
  { name: "Zoom", Logo: ZoomLogo, wordmark: true },
  { name: "Teams", Logo: MicrosoftTeamsLogo },
  { name: "Meet", Logo: GoogleMeetLogo },
];
const SEND_APPS = [
  { name: "WhatsApp", Logo: WhatsappLogo },
  { name: "Email", Logo: EnvelopeSimple },
  { name: "LinkedIn", Logo: LinkedinLogo },
  { name: "YouTube", Logo: YoutubeLogo },
];

// How long the visitor sees themselves in the studio before it offers them
// the way to record for real.
export const SEEN_MS = 5000;

// "Personalizza il tuo studio": a live preview of a branded AVRAH scene. It
// waits in grey for the visitor to see themselves in it with their webcam,
// in front of their own room, as the product films anyone without a green
// screen. Once they have for SEEN_MS, under it, the way to record a video
// like it for real, for the `others` the hero says don't see them; then
// where the video goes. Its heading is where the page's links to try a video
// land: from 961px the heading and its line run across the top, and under
// them the controls beside the frame, which keeps in view, where the video
// goes under it, while the visitor works down the controls.
export default function StudioSection({ others }) {
  useStudioFace();
  const camera = useCamera();
  const seen = useSeenFor(camera.status === "on", SEEN_MS);
  const content = useContent();
  const { ROLES, BRAND_COLORS } = content;
  const lang = useHomeLang();
  const templates = STUDIO_TEMPLATES[lang];
  const words = WORDS[lang];
  const [look, setLook] = useState(() => firstLook(content, templates));
  if (!(Number.isInteger(others) && others > 0)) throw new Error(`StudioSection needs how many clients don't see the visitor, not ${others}`);
  const { template, name, role, ownRole, disclaimer, color } = look;
  const set = (key) => (value) => setLook({ ...look, [key]: value });
  const setTemplate = set("template");
  const chosen = templates.find((t) => t.index === template);

  return (
    <section aria-labelledby="prova-video" className="hf-sec hf-wrap">
      <div className="st">
        <h2 id="prova-video" className="t-h2 st-h">{words.heading}</h2>
        <p className="t-lead st-lead">{words.lead}</p>
        <form className="st-form" onSubmit={(e) => e.preventDefault()}>
          <label className="st-field">
            {words.name}
            <input type="text" placeholder={SAMPLE_NAME} value={name} onChange={(e) => set("name")(e.target.value)} autoComplete="name" />
          </label>
          <div className="st-field">
            <label className="st-field">
              {words.role}
              <select value={role} onChange={(e) => set("role")(Number(e.target.value))}>
                {ROLES.map((r, i) => <option key={r} value={i}>{r}</option>)}
                <option value={OWN_ROLE}>{words.otherRole}</option>
              </select>
            </label>
            {role === OWN_ROLE && (
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the visitor just asked to write it
              <input type="text" aria-label={words.ownRole} placeholder={SAMPLE_ROLE} value={ownRole} onChange={(e) => set("ownRole")(e.target.value)} autoFocus />
            )}
          </div>
          <div className="st-field" role="group" aria-label={words.template}>
            <span>{words.template}</span>
            <div className="st-tiles">
              {templates.map((t) => (
                <button
                  key={t.index}
                  type="button"
                  className={t.index === template ? "st-tile st-tile-on" : "st-tile"}
                  aria-pressed={t.index === template}
                  onClick={() => setTemplate(t.index)}
                >
                  <t.Icon size={22} weight={t.index === template ? "fill" : "regular"} aria-hidden="true" />
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="st-field" role="group" aria-label={words.brand}>
            <span>{words.brand}</span>
            <div className="st-swatches">
              {BRAND_COLORS.map((c, i) => (
                <button
                  key={c.hex}
                  type="button"
                  className={i === color ? "st-sw st-sw-on" : "st-sw"}
                  style={{ background: c.hex }}
                  aria-label={words.swatch(c.name)}
                  aria-pressed={i === color}
                  onClick={() => set("color")(i)}
                />
              ))}
            </div>
          </div>
          <label className="st-field">
            {words.disclaimer}
            <textarea rows={4} value={disclaimer} onChange={(e) => set("disclaimer")(e.target.value)} />
          </label>
        </form>

        <div className="st-out">
          <div className="st-shot">
            {camera.status === "on" && <PrompterStrip lines={chosen.prompt} />}
            <div className="st-view" data-camera={camera.status}>
              <LookScene look={look} camera={camera} />
              <CameraControl camera={camera} words={words} />
            </div>
          </div>
          {/* Right under the studio the visitor has seen themselves in, the
              way to record in it for real. */}
          {seen && (
            <div className="st-go">
              <p className="st-go-line">{words.seenBy(others)}</p>
              <Link className="btn btn-gold st-rec" to="/signup">
                <span className="st-rec-dot" aria-hidden="true" />
                {words.record}
              </Link>
            </div>
          )}
          <div className="st-uses">
            <Uses label={words.live} apps={LIVE_APPS} />
            <Uses label={words.sent} apps={SEND_APPS} />
          </div>
        </div>
      </div>
    </section>
  );
}

// Whether `on` has held for `ms` together, at any time since mounting: once
// it has, it stays so.
// The frame's face, IBM Plex Sans, fetched once the page has settled, so it
// is in before the visitor reaches a stage, which asks for it only as they
// near it (studio.css). Where the browser cannot load faces itself, the
// stage asks for it as it shows.
const STUDIO_FACES = ["400 1em 'IBM Plex Sans'", "700 1em 'IBM Plex Sans'"];
function useStudioFace() {
  useEffect(() => {
    if (!document.fonts) return;
    pageSettled()
      .then(() => Promise.all(STUDIO_FACES.map((face) => document.fonts.load(face))))
      .catch((error) => console.error("The studio's face did not load, so its stages are set in the fallback until it does:", error));
  }, []);
}

function useSeenFor(on, ms) {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen || !on) return undefined;
    const timer = setTimeout(() => setSeen(true), ms);
    return () => clearTimeout(timer);
  }, [seen, on, ms]);
  return seen;
}

// On the frame: until the camera is on, the way to see yourself in it and
// what happens to the picture, in its middle; once it is, the way to turn it
// off.
function CameraControl({ camera, words }) {
  const { status, problem, start, stop } = camera;
  if (status === "on") {
    return (
      <button type="button" className="st-off" onClick={stop} aria-label={words.turnOff}>
        <CameraSlash size={18} weight="bold" aria-hidden="true" />
        <span>{words.turnOff}</span>
      </button>
    );
  }
  return (
    <div className="st-ask">
      <button type="button" className="btn st-ask-b" onClick={start} disabled={status === "asking"}>
        <Camera size={20} weight="bold" aria-hidden="true" />
        {status === "asking" ? words.asking : status === "problem" ? words.retry : words.tryIt}
      </button>
      <p className="st-ask-note" role={problem ? "alert" : undefined}>
        {problem ? problemIn(words, problem) : words.private}
      </p>
    </div>
  );
}

function problemIn(words, reason) {
  const said = words.problems[reason];
  if (!said) throw new Error(`The studio has no words for the camera problem "${reason}"`);
  return said;
}

function Uses({ label, apps }) {
  return (
    <div className="st-use">
      <span className="st-use-l">{label}</span>
      <ul className="st-apps">
        {apps.map(({ name, Logo, wordmark }) => (
          <li key={name} className={wordmark ? "st-app st-app-word" : "st-app"}>
            {wordmark ? <Logo size={44} /> : <><Logo size={20} weight="fill" aria-hidden="true" /><span>{name}</span></>}
          </li>
        ))}
      </ul>
    </div>
  );
}
